import { FocusEvent } from "react";
import {
    IOtpForm, useCloseOtpModal, useGetOtpModalInputRefs, useModalOtpData, useSetBlurAndError,
    useSetModalOtpStep, useSetOtpModalAlertState } from "../stores/otpModal.store";
import useOtpModalApi from "../axios/useOtpModal.api";
import { ActionPortalTypes } from "../types/actionPortal.type";
import { useProjectConfigContext } from "../contexts/ProjectConfigContext";
import useHomePageApi from "../axios/useHomePage.api";
import { RequestStatusValue, useOpenCheckReqStatusModal } from "../stores/checkReqStatusModal.store";
import { useOpenPermitValidationDateModal } from "../stores/permitValidationDateModal.store";
import { useOpenModalRenewedPermit } from "../stores/renewedPermitModal.store";
import OwnershipCarType from "../types/ownershipCar.type";
import { useOpenAddFileModal } from "../stores/addFileModal.store";
import { useOpenCancelPermitModalData } from "../stores/permitIsCanceledModal.store";
import { useOpenVehiclePermitModal } from "../stores/updateVehiclePermitModal.store";
import { ONLY_DIGIT_PERMITED_PATH, REQUIRED_FIELD_PATH } from "../validations/errorMessages";


const useOtpModalFunctions = () => {

    const refs = useGetOtpModalInputRefs();
    const { projectConfigState } = useProjectConfigContext();
    const { cancelPermitOtpApi } = useHomePageApi();
    const { checkOtpCodeApi, checkPermitValidationDateOtpApi,
        checkRequestStatusOtpApi, sendOtpCodeApi, preRenewedPermitOtpApi, preUpdateVehicleOtpApi } = useOtpModalApi();

    const setBlurAndError = useSetBlurAndError();
    const setModalOtpStep = useSetModalOtpStep();
    const modalOtpData = useModalOtpData();
    const setOtpModalAlertState = useSetOtpModalAlertState();

    const openModalRenewedPermit = useOpenModalRenewedPermit();
    const openCheckReqStatusModal = useOpenCheckReqStatusModal();
    const openPermitValidationDateModal = useOpenPermitValidationDateModal();
    const openAddFileModal = useOpenAddFileModal();
    const openCancelPermitModal = useOpenCancelPermitModalData();
    const openVehiclePermitModal = useOpenVehiclePermitModal();

    const closeOtpModal = useCloseOtpModal();


    const { homePageConfig } = projectConfigState;
    const sendingCitizenOtpCodeVia = homePageConfig.sendingCitizenOtpCodeVia;
    const { values } = modalOtpData.otpForm;
    const { portalAction } = modalOtpData;


    /**
     * Handles the blur event for OTP input fields.
     * @param event -The focus event from the input field.
     */
    function handleBlurOtpInput(event: FocusEvent<HTMLInputElement>) {

        const field = event.target.name as keyof IOtpForm['values'];
        const value = event.target.value;

        const errors = { ...modalOtpData.otpForm.errors };
        let hasError = false;
        delete errors[field];


        // This field is required
        if (value.trim() === '') {
            hasError = true;
            errors[field] = REQUIRED_FIELD_PATH;
        }

        // Vehicle Number must contain only alphanumeric characters
        if (!hasError && !/^[0-9]+$/.test(value)) {
            errors[field] = ONLY_DIGIT_PERMITED_PATH;
        }

        setBlurAndError(field, errors);
    }

    async function handleCheckOtpCode() {

        console.log('handleCheckOtpCode called with portalAction:', portalAction);

        console.log('Otp code: ' + getOtpCode());

        if (portalAction === ActionPortalTypes.ADD_FILE_TO_REQUEST) {

            handlePreAddFileOtpCode();
        } else if (portalAction === ActionPortalTypes.CHECK_REQUEST_STATUS) {

            handleCheckRequestStatusOtp();
        } else if (portalAction === ActionPortalTypes.CANCEL_PERMIT) {

            handleCancelPermitOtp();
        } else if (portalAction === ActionPortalTypes.CHECK_PEMIT_VALIDITY) {

            handleCheckPermitValidationDateOtp();
        } else if (portalAction === ActionPortalTypes.UPDATE_VEHICLE_DETAILS) {

            handlePreUpdateVehicleOtp();
        } else if (portalAction === ActionPortalTypes.RENEWED_PERMIT_OPTION) {

            handlePreRenewedPermitOtp();
        }

    }

    async function handleSendOtpCode() {

        loadingModal();

        const { personId, vehicleNum } = values;
        let isPermit = true;

        if (portalAction === ActionPortalTypes.ADD_FILE_TO_REQUEST || portalAction === ActionPortalTypes.CHECK_REQUEST_STATUS) {
            isPermit = false;
        }

        const dataToSend = {
            isPermit: isPermit,
            personId: personId,
            vehicleNum: vehicleNum,
            portalAction: portalAction,
            otpCode: getOtpCode(),
            sendOtpCodeVia: sendingCitizenOtpCodeVia,
        };

        const response = await sendOtpCodeApi(dataToSend);

        if (!response.isSuccess) {

            setDangerAlertModal(response.message);
            return;
        }
   
        const mobileOrEmailForGUI = response.mobileOrEmailForGUI;
        const sendVia = response.sendVia;

        notLoadingModal();
        setModalOtpStep('otp-input', mobileOrEmailForGUI, sendVia);
    }

    async function handleCheckRequestStatusOtp() {
        loadingModal();

        const personId = values.personId;
        const vehicleNum = values.vehicleNum;
        const otpCode = getOtpCode();

        const response = await checkRequestStatusOtpApi({
            personId: personId,
            vehicleNum: vehicleNum,
            otpCode: otpCode,
            isPermit: false
        });

        if (!response.isSuccess) {

            setDangerAlertModal(response.message);
            return;
        }

        const requestStatus = response.requestStatus as RequestStatusValue;

        openCheckReqStatusModal(requestStatus);
        closeOtpModal();
        notLoadingModal();

        return true;
    }


    return {
        handleBlurOtpInput,
        handleCheckRequestStatusOtp,
        handleSendOtpCode,
        handleCheckOtpCode
    };


    async function handleCheckPermitValidationDateOtp() {

        const response = await checkPermitValidationDateOtpApi({
            personId: values.personId,
            vehicleNum: values.vehicleNum,
            otpCode: getOtpCode(),
            isPermit: true
        });

        if (!response.isSuccess) {

            setDangerAlertModal(response.message);
            return;
        }

        notLoadingModal();
        closeOtpModal();
        openPermitValidationDateModal(response.dateValidationPeriod);
    }

    async function handleCancelPermitOtp() {

        loadingModal();

        const response = await cancelPermitOtpApi({
            personId: values.personId,
            vehicleNum: values.vehicleNum,
            otpCode: getOtpCode(),
            isPermit: true
        });

        if (!response.isSuccess) {

            setDangerAlertModal(response.message);
            return;
        }

        notLoadingModal();
        closeOtpModal();
        openCancelPermitModal();
    }

    async function handlePreAddFileOtpCode() {

        loadingModal();

        const personId = values.personId;
        const vehicleNum = values.vehicleNum;
        const otpCode = getOtpCode();

        const response = await checkOtpCodeApi({
            personId: personId,
            vehicleNum: vehicleNum,
            otpCode: otpCode,
            isPermit: false
        });

        if (!response.isSuccess || !response.isOtpCodeValid) {

            setDangerAlertModal(response.message);
            return;
        }

        notLoadingModal();

        openAddFileModal(personId, vehicleNum, otpCode);
        closeOtpModal();
    }

    async function handlePreUpdateVehicleOtp() {

        loadingModal();

        const personId = values.personId;
        const vehicleNum = values.vehicleNum;
        const otpCode = getOtpCode();

        const response = await preUpdateVehicleOtpApi({
            personId: personId,
            vehicleNum: vehicleNum,
            otpCode: otpCode,
            isPermit: true
        });

        if (!response.isSuccess) {

            setDangerAlertModal(response.message);
            return;
        }

        notLoadingModal();
        closeOtpModal();
        openVehiclePermitModal(personId, vehicleNum, otpCode);
    }

    async function handlePreRenewedPermitOtp() {

        loadingModal();

        const response = await preRenewedPermitOtpApi({
            personId: values.personId,
            vehicleNum: values.vehicleNum,
            otpCode: getOtpCode(),
        });

        if (!response.isSuccess) {

            setDangerAlertModal(response.message);
            return;
        }

        notLoadingModal();

        openModalRenewedPermit({
            endValidityDate: response.endValidityDate,
            permitId: response.permitId,
            citizenComment: `תשלום עבור חידוש תו: ${response.permitFee} ש"ח`,
            isCompany: response.isCompany ? OwnershipCarType.COMPANY : OwnershipCarType.PRIVATE,
            permitFee: response.permitFee,
            personId: values.personId,
            vehicleNum: values.vehicleNum,
            otpCode: getOtpCode(),
        });

        closeOtpModal();
    }

    function getOtpCode() {

        let otpCode = '';

        refs.forEach(ref => {
            if (ref.current) {
                otpCode += ref.current.value;
            }
        });

        return otpCode;
    }

    function loadingModal() {
        setOtpModalAlertState({ alertStatus: 'loading', message: '', alertColor: '' });
    }

    function notLoadingModal() {
        setOtpModalAlertState({ alertStatus: 'noAlert', message: '', alertColor: '' });
    }

    function setDangerAlertModal(message: string) {
        setOtpModalAlertState({ alertStatus: 'hasAlert', message: message, alertColor: 'danger' });
    }
}

export default useOtpModalFunctions;